import { lazy, useState, useMemo } from 'react';
import { formatClock, formatUtcTime, formatTimeUntilReset } from '../utils/time';
import { ThemeToggleCustom } from './ThemeToggleCustom';
import SVG  from './IcoComponents';
import { useTheme } from '../store/theme';
import { useNow, usePreciseNow } from '../store/now';
import { Tooltip } from 'react-tooltip';

const AnimatedTooltip = lazy(() =>
  import('./ui/shadcn-io/animated-tooltip').then((module) => ({
    default: module.AnimatedTooltip,
  }))
);

export function Header({ onOpenSettings, modalOpen }: { onOpenSettings: () => void; modalOpen: boolean }) {
  const [isSpinning, setIsSpinning] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const { theme } = useTheme();
  const now = useNow();
  const preciseNow = usePreciseNow();

  const LogoMetas = [
    {
      id: 1,
      name: "brought to you with 💖",
      image: theme === 'dark' ? "/logo=darkmode.svg" : "/logo.svg",
    },
  ];

  // Timer removed - now passed as prop from parent

  const handleSettingsClick = () => {
    setIsSpinning(true);
    onOpenSettings();
    
    // Reset spinning state after animation completes
    setTimeout(() => {
      setIsSpinning(false);
    }, 600); // Animation duration
  };

  // Memoize tooltip content to prevent unnecessary recalculations
  const tooltipContent = useMemo(() => {
    if (!isTooltipVisible) return null;
    return (
      <div className="flex flex-col gap-1">
        <div>UTC: {formatUtcTime(preciseNow)}</div>
        <div>Reset in: {formatTimeUntilReset(preciseNow)}</div>
      </div>
    );
  }, [preciseNow, isTooltipVisible]);

  return (
    <header className="sticky top-0 pt-2 pb-0 z-12 bg-transparent backdrop-blur-xs">
      <div className="flex w-full items-center justify-between px-3 sm:px-6">
        <div className="flex-initial scale-110 sm:scale-120">
          <a href="/">
            <AnimatedTooltip items={LogoMetas} />
          </a>
        </div>
        <div className="flex place-content-center gap-3 sm:gap-4">
          <span 
            data-tooltip-id="clock-tooltip"
            onMouseEnter={() => setIsTooltipVisible(true)}
            onMouseLeave={() => setIsTooltipVisible(false)}
            className="flex-auto content-center text-shadow-[var(--shadow-clock)] text-lg sm:text-lg [color:var(--color-text-default)] [font-family:var(--font-family-inconsolata)] cursor-help"
          >
            {formatClock(now)}
          </span>
          <Tooltip
            id="clock-tooltip"
            border="1px solid var(--color-fg-secondary)"
            className="z-9999"
            place="bottom"
            delayShow={300}
            delayHide={100}
            offset={8}
            style={{
              backgroundColor: 'var(--color-fg-secondary)',
              color: 'var(--color-text-emphasis)',
              fontSize: '12px',
              fontFamily: 'var(--font-family-inconsolata)',
              padding: '6px 8px 4px 8px',
              boxShadow: 'var(--shadow-button)',
              maxWidth: '200px',
            }}
          >
            {tooltipContent}
          </Tooltip>
          <span className="flex-auto pt-2.5 sm:pt-2 content-center">
            <ThemeToggleCustom />
            </span>
          <button 
            onClick={handleSettingsClick} 
            className={`place-items-center transition-transform ease-in-out ${
              modalOpen 
                ? 'place-items-center rotate-45 [fill:var(--color-text-default)]' 
                : '[stroke:var(--color-text-default)] hover:[fill:var(--color-text-default)] hover:rotate-45'
            } ${
              isSpinning 
                ? 'animate-spin-360' 
                : ''
            }`}
            style={{
              animation: isSpinning ? 'spin-360 0.4s ease-in-out' : undefined,
              willChange: isSpinning ? 'transform' : undefined
            }}
          >
            {modalOpen ? <SVG.IcoSettingsPressed /> : <SVG.IcoSettingsDefault />}
          </button>
        </div>
      </div>
    </header>
  );
}
